<template>
  <div class="d-flex flex-column">
    <v-autocomplete
      :model-value="modelValue"
      @update:model-value="fetchFullAddress"
      v-bind="{ ...$props, ...$attrs }"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      :items="items"
      density="compact"
      :loading="isLoading"
      @update:search="handleSearchUpdate"
      color="primary"
      :hide-no-data="hideNoData"
      hide-selected
      :label="translatedLabel"
      :placeholder="`${$t(
        'forms.elements.selects.addressAutocompleteField.placeholders.searchWarehouseAddress'
      )}...`"
      item-title="address"
      return-object
      no-filter
      :data-testid="$attrs['test-id']">
      <template v-slot:no-data>
        <v-container fluid class="pl-4 pb-0">
          <p>
            {{
              isLoading
                ? `${$t(
                    'forms.elements.selects.addressAutocompleteField.messages.loadingSuggestions'
                  )}...`
                : `${$t(
                    'forms.elements.selects.addressAutocompleteField.messages.noAddressFound'
                  )}...`
            }}
          </p>
        </v-container>
      </template>
      <template #label v-if="required">
        <div :class="{ 'with-tooltip': tooltipText }">
          {{ translatedLabel }}
          <help-icon-tooltip v-if="tooltipText" iconStyleClass="grey darken-2">
            {{ tooltipText }}
          </help-icon-tooltip>
          <span class="text-red ml-2"><strong>*</strong></span>
        </div>
      </template>
    </v-autocomplete>
    <div class="enter-manually-btn d-flex align-end justify-end">
      <a href="#" @click="manualOverride" v-if="!readonly">
        {{ $t('forms.elements.selects.addressAutocompleteField.buttons.enterAddressManually') }}
      </a>
    </div>
  </div>
</template>

<script>
import { debounce } from 'debounce';

export default {
  props: {
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: false
    },
    country: {
      type: String,
      default: 'USA'
    },
    modelValue: {
      type: Object,
      default: null
    },
    clearOnCountryChange: {
      type: Boolean,
      default: false
    },
    tooltipText: {
      type: String,
      required: false,
      default: null
    }
  },
  data: function () {
    return {
      items: [],
      isLoading: false,
      hideNoData: true
    };
  },
  computed: {
    translatedLabel() {
      return (
        this.label ??
        this.$t('forms.elements.selects.addressAutocompleteField.labels.searchWarehouseAddress')
      );
    }
  },
  methods: {
    async fetchFullAddress(value) {
      if (value?.placeId) {
        this.isLoading = true;
        try {
          const exactAddress = await axios.get(`/geocoding/${value.placeId}`);
          this.$emit('filled', exactAddress?.data);
        } catch (e) {
          if (e.response?.status === 404) {
            this.$emit('update:model-value', null); // Clear field
            this.manualOverride();
          } else {
            throw e;
          }
        } finally {
          this.isLoading = false;
        }
      }
    },
    async getItems(searchVal) {
      this.hideNoData = false;
      if (searchVal && searchVal.length >= 3) {
        this.hideNoData = false;
        this.isLoading = true;
        try {
          const matches = await axios.get(`/geocoding?locationType=address`, {
            params: {
              address: searchVal,
              country: this.country
            }
          });

          this.items = matches?.data?.data;
        } finally {
          this.isLoading = false;
        }
      }
    },
    manualOverride(e) {
      e?.preventDefault();
      this.$emit('manual-override');
    },
    handleSearchUpdate: debounce(function (newVal) {
      this.getItems(newVal);
    }, 200)
  },
  watch: {
    country(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.items = [];
        if (this.clearOnCountryChange) {
          this.$emit('input', null);
        }
      }
    }
  }
};
</script>

<style scoped>
.enter-manually-btn {
  width: 100%;
  z-index: 3;
  margin-top: -16px;
  font-size: 14px;
}
</style>
